import { useState } from 'react'
import { useDashboardStore } from '@/store/dashboard'
import { apiPost } from '@/lib/api'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { ModelIcon } from '@/components/ModelIcon'
import {
  AI_MODELS,
  ENABLED_MODELS,
  MODEL_CREDIT_COST,
  MODEL_LABELS,
  PROMPT_MAX_LENGTH,
} from '@ekorank/shared'
import type { AIModel, CheckInterval } from '@ekorank/shared'
import { z } from 'zod'

const INDUSTRIES = [
  'Marketing', 'SaaS', 'E-commerce', 'Legal', 'Healthcare',
  'Finance', 'Education', 'Real Estate', 'Consulting', 'Other',
]

const ApiOnboardResponse = z.object({
  ok: z.literal(true),
  data: z.object({ brandId: z.string(), jobId: z.string(), message: z.string() }),
})

interface Props {
  onClose: (newBrandId?: string) => void
}

export function AddBrandModal({ onClose }: Props) {
  const fetchResults = useDashboardStore((s) => s.fetchResults)

  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Step 1 — Brand
  const [brandName, setBrandName] = useState('')
  const [brandUrl, setBrandUrl] = useState('')
  const [industry, setIndustry] = useState('')

  // Step 2 — Prompts
  const [prompts, setPrompts] = useState<string[]>([])
  const [promptInput, setPromptInput] = useState('')

  // Step 3 — Models
  const [selectedModels, setSelectedModels] = useState<AIModel[]>(['claude-haiku'])

  const addPrompt = () => {
    const p = promptInput.trim()
    if (p.length >= 10 && !prompts.includes(p)) {
      setPrompts((prev) => [...prev, p])
      setPromptInput('')
    }
  }

  const toggleModel = (model: AIModel) => {
    setSelectedModels((prev) =>
      prev.includes(model) ? prev.filter((m) => m !== model) : [...prev, model],
    )
  }

  const handleSubmit = async () => {
    if (selectedModels.length === 0) { setError('Select at least one AI model'); return }
    if (prompts.length === 0) { setError('Add at least one prompt'); return }

    setLoading(true)
    setError(null)
    const result = await apiPost('/api/onboard', ApiOnboardResponse, {
      brandName,
      brandUrl: brandUrl || undefined,
      industry,
      prompts,
      models: selectedModels,
      checkInterval: 'weekly' as CheckInterval,
    })
    setLoading(false)
    if (!result.ok) { setError(result.error); return }

    const newBrandId = result.data.data.brandId
    void fetchResults(newBrandId)
    onClose(newBrandId)
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-md bg-surface-container border border-outline-variant/20 rounded-xl p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-white">Add Brand</h2>
          <div className="flex items-center gap-4">
            <span className="text-xs font-mono text-outline">Step {step} / 3</span>
            <button
              onClick={() => onClose()}
              className="w-10 h-10 flex items-center justify-center rounded-lg text-outline hover:text-white hover:bg-white/5 transition-colors text-xl leading-none"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className="h-1 bg-surface-variant rounded-full overflow-hidden mb-6">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${(step / 3) * 100}%` }}
          />
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-error/10 border border-error/30 text-error text-sm">
            {error}
          </div>
        )}

        {/* Step 1 — Brand */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-mono text-outline uppercase tracking-widest mb-2">Brand Name</label>
              <input
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                className="w-full bg-background border border-outline-variant/20 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-primary/50"
                placeholder="Acme Inc."
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-mono text-outline uppercase tracking-widest mb-2">Website URL (optional)</label>
              <input
                value={brandUrl}
                onChange={(e) => setBrandUrl(e.target.value)}
                className="w-full bg-background border border-outline-variant/20 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-primary/50"
                placeholder="https://acme.com"
              />
            </div>
            <div>
              <label className="block text-xs font-mono text-outline uppercase tracking-widest mb-2">Industry</label>
              <select
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="w-full bg-background border border-outline-variant/20 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-primary/50"
              >
                <option value="">Select industry...</option>
                {INDUSTRIES.map((ind) => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => { setError(null); setStep(2) }}
              disabled={!brandName.trim() || !industry}
              className="w-full py-3 rounded-lg bg-gradient-to-r from-primary to-secondary text-on-primary-container font-bold disabled:opacity-50 mt-2"
            >
              Continue
            </button>
          </div>
        )}

        {/* Step 2 — Prompts */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-white mb-1">Prompts to track</p>
              <p className="text-xs text-on-surface-variant mb-3">
                Natural language questions you want AI models to answer (e.g. "What's the best tax firm in Idaho?")
              </p>
            </div>
            <div className="space-y-2">
              <textarea
                value={promptInput}
                onChange={(e) => setPromptInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addPrompt() }
                }}
                rows={2}
                maxLength={PROMPT_MAX_LENGTH}
                className="w-full bg-background border border-outline-variant/20 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-primary/50 resize-none"
                placeholder="What's the best tax firm in Idaho?"
                autoFocus
              />
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-outline">{promptInput.length} / {PROMPT_MAX_LENGTH}</span>
                <button
                  onClick={addPrompt}
                  disabled={promptInput.trim().length < 10}
                  className="px-4 py-1.5 rounded-lg bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors disabled:opacity-40 text-sm"
                >
                  Add
                </button>
              </div>
            </div>
            <div className="space-y-1.5 max-h-36 overflow-y-auto">
              {prompts.map((p, i) => (
                <div key={i} className="flex items-start gap-2 px-3 py-2 rounded-lg bg-surface-container-high border border-outline-variant/20">
                  <span className="text-xs text-on-surface flex-1 leading-snug">{p}</span>
                  <button onClick={() => setPrompts((prev) => prev.filter((_, j) => j !== i))} className="text-outline hover:text-error text-xs shrink-0 mt-0.5">×</button>
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-2">
              <button onClick={() => setStep(1)} className="flex-1 py-2.5 rounded-lg border border-outline-variant/30 text-white text-sm hover:bg-white/5 transition-colors">
                Back
              </button>
              <button
                onClick={() => { setError(null); setStep(3) }}
                disabled={prompts.length === 0}
                className="flex-1 py-2.5 rounded-lg bg-gradient-to-r from-primary to-secondary text-on-primary-container font-bold disabled:opacity-50 text-sm"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Models */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-white mb-1">AI Models to query</p>
              <p className="text-xs text-on-surface-variant">Cost per check = prompts × model credits</p>
            </div>
            <div className="space-y-2">
              {AI_MODELS.map((model) => {
                const enabled = ENABLED_MODELS.has(model)
                const checked = selectedModels.includes(model)
                const creditCost = MODEL_CREDIT_COST[model]
                return (
                  <label
                    key={model}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border transition-colors ${
                      !enabled
                        ? 'border-outline-variant/5 opacity-40 cursor-not-allowed'
                        : checked
                          ? 'border-primary/40 bg-primary/5 cursor-pointer'
                          : 'border-outline-variant/10 hover:border-outline-variant/20 cursor-pointer'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => enabled && toggleModel(model)}
                      disabled={!enabled}
                      className="accent-primary"
                    />
                    <span className="text-sm text-white flex-1 inline-flex items-center gap-2">
                      <ModelIcon model={model} size={14} />
                      {MODEL_LABELS[model]}
                    </span>
                    {!enabled && <span className="text-[10px] font-mono text-outline/50 mr-1">soon</span>}
                    <span className="text-xs font-mono text-outline">{creditCost} cr</span>
                  </label>
                )
              })}
            </div>
            <div className="flex gap-3 mt-2">
              <button onClick={() => setStep(2)} className="flex-1 py-2.5 rounded-lg border border-outline-variant/30 text-white text-sm hover:bg-white/5 transition-colors">
                Back
              </button>
              <button
                onClick={() => void handleSubmit()}
                disabled={loading || selectedModels.length === 0}
                className="flex-1 py-2.5 rounded-lg bg-gradient-to-r from-primary to-secondary text-on-primary-container font-bold disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
              >
                {loading ? <LoadingSpinner size="sm" /> : 'Start Tracking'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
